import React from 'react';
import { useLocation } from '@remix-run/react';
import { Button, pxToRem, useStyles, useTheme } from '@archipro-design/aria';
import { ArrowLeftSLine } from '@archipro-design/icons';
import * as S from './DetailBannerSection.style';
import type {
    ColumnDimensionType,
    TopBanner,
} from '~/modules/page-builder/types';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import { useClickBack } from '~/modules/root/hook/use-click-back';
import VideoBannerWithOptimistic from '@modules/page-builder/component/edit/edit-top-banner/VideoBannerWithOptimistic';
import ImageCaption from '~/modules/page-builder/component/common/image-caption/ImageCaption';
import { ImageCaptionContainer } from '@modules/page-builder/component/detail/detail-single-image-shell/DetailSingleImageShell.style';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { useFeature } from '@modules/root';

interface DetailBannerSectionProps extends TopBanner {
    dimensions: ColumnDimensionType;
    size: string;
    title?: string;
    actions?: React.ReactNode[];
    showBackButton?: boolean;
    showActionsOnHover?: boolean;
    caption?: string;
    awards?: AwardProps[];
    customOverlayElement?: React.ReactNode;
    showOverlay?: boolean;
    autoPlayVideoBanner?: boolean;
    loopVideoBanner?: boolean;
    playsInlineVideoBanner?: boolean;
    hideVideoControls?: boolean;
    showCaption?: boolean;
}

const DetailBannerSection = ({
    dimensions,
    imageUrl,
    introText,
    size,
    title,
    showBackButton,
    showActionsOnHover = false,
    videoBannerUrl,
    isVideoBanner,
    caption,
    awards,
    customOverlayElement,
    showOverlay = false,
    autoPlayVideoBanner,
    loopVideoBanner,
    playsInlineVideoBanner = true,
    hideVideoControls = false,
    showCaption = true,
}: DetailBannerSectionProps) => {
    const { css } = useStyles({
        dimension: dimensions,
        showActionsOnHover,
        showOverlay,
    });
    const theme = useTheme();
    const enableAwardBadges = useFeature('award_badges');

    const { onClick: onClickBack } = useClickBack();

    const altText =
        caption ?? introText ?? (title ? `${title} banner` : 'banner');

    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);

    // inline autoplay (default)
    const urlVideoParam = urlSearchParams.get('video');
    const autoPlayBanner = Number(urlVideoParam) === 1;

    // modal autoplay
    const urlModalVideoParam = urlSearchParams.get('modal-video');
    const autoPlayModalBanner = Number(urlModalVideoParam) === 1;

    return (
        <>
            <div id="top-banner" className={css(S.BannerWrapper)}>
                <div className={css(S.ImageContainer)}>
                    {imageUrl && (
                        <>
                            {isVideoBanner && videoBannerUrl ? (
                                <VideoBannerWithOptimistic
                                    dimension={dimensions}
                                    videoBannerUrl={videoBannerUrl}
                                    imageUrl={imageUrl}
                                    playsInline={
                                        !autoPlayModalBanner ||
                                        playsInlineVideoBanner
                                    }
                                    autoplay={
                                        autoPlayBanner ||
                                        autoPlayModalBanner ||
                                        autoPlayVideoBanner
                                    }
                                    loop={loopVideoBanner}
                                    hideControls={hideVideoControls}
                                />
                            ) : (
                                <ImageWithPaintPicker
                                    key={imageUrl}
                                    src={imageUrl}
                                    layout={'fill'}
                                    placeholderColor="transparent"
                                    width={dimensions.width}
                                    height={dimensions.height}
                                    loading={'eager'}
                                    fetchPriority="high"
                                    sizes={size}
                                    objectFit={'cover'}
                                    alt={altText}
                                    fadeIn={false}
                                    className="group"
                                />
                            )}
                        </>
                    )}
                    {showBackButton && (
                        <>
                            <Button
                                as="a"
                                transparent={true}
                                icon={
                                    <ArrowLeftSLine
                                        variables={{
                                            color: theme.siteVariables.colors
                                                .white[100],
                                        }}
                                    />
                                }
                                variables={{
                                    buttonPadding: pxToRem(12),
                                    iconPadding: 0,
                                }}
                                size={30}
                                className={css(S.BackButton)}
                                onClick={onClickBack}
                            />
                        </>
                    )}
                    {enableAwardBadges && awards && awards.length > 0 && (
                        <BannerAwards awards={awards} />
                    )}
                </div>
            </div>
            {caption && showCaption && (
                <div className={css(S.MobileCaptionContainer)}>
                    {caption && (
                        <div className={css(ImageCaptionContainer)}>
                            <ImageCaption text={caption} isMain={true} />
                        </div>
                    )}
                </div>
            )}
            {customOverlayElement}
        </>
    );
};

export default DetailBannerSection;
